import React, { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "../components/shared/Sidebar";
import Navbar from "../components/shared/Navbar";
import { useWS } from "../context/WebSocketContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Webhook, Copy, CheckCircle2, QrCode, MessageCircle, Instagram, Facebook,
  Calendar, Mail, Zap, RefreshCw, Link2, Save, ShieldCheck, Info, ExternalLink,
  Plus, Smartphone, Wifi, WifiOff, Trash2, Loader2, X, AlertCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import ProductivitySettings from "../components/settings/ProductivitySettings";
import MassCampaignSettings from "../components/settings/MassCampaignSettings";
import MetaIntegrationCard from "../components/settings/MetaIntegrationCard";
import HelpTooltip from "../components/shared/HelpTooltip";
import TourRestartButton from "../components/shared/TourRestartButton";
import { useProductTour } from "../hooks/useProductTour";
import { FEATURE_KEYS, hasFeature, isSuperAdmin, normalizeRole } from "../lib/features";

const FIXED_META_VERIFY_TOKEN = "cliver_123";
const PUBLIC_WEBHOOK_BASE_URL = "https://api.clivercrm.com.br";

// Passos do tour guiado de Configurações -- um tour só cobrindo a ideia geral
// das 3 abas (em vez de um tour por aba), pra não depender de trocar de aba
// programaticamente no meio do tour. Todos os alvos existem na aba "Canais"
// (a aba padrão ao abrir a tela), então funciona sem nenhuma orquestração.
const SETTINGS_TOUR_STEPS = [
  {
    element: '[data-testid="settings-tabs-list"]',
    popover: {
      title: "3 áreas de Configurações",
      description: "Canais (conectar WhatsApp/Instagram/Facebook), Produtividade (campos personalizados e templates) e Envio em Massa (campanhas) -- cada uma na sua aba.",
    },
  },
  {
    element: '[data-testid="tab-channels"]',
    popover: {
      title: "Canais",
      description: "Conecte seus números de WhatsApp (e Instagram/Facebook, se disponível), defina quem é o responsável por cada canal e se ele é compartilhado com o time.",
    },
  },
  {
    element: '[data-testid="tab-integrations"]',
    popover: {
      title: "Produtividade",
      description: "Campos Personalizados (dados extras do seu negócio nos leads) e Templates de 1 Clique (respostas rápidas com placeholders) ficam aqui.",
    },
  },
  {
    element: '[data-testid="tab-mass-campaigns"]',
    popover: {
      title: "Envio em Massa",
      description: "Crie campanhas de WhatsApp pra vários contatos de uma vez, com opção de agendar o disparo.",
    },
  },
  {
    element: '[data-testid="section-native-whatsapp"]',
    popover: {
      title: "Conecte seu WhatsApp",
      description: "Comece por aqui: escaneie o QR Code com o WhatsApp do seu celular pra conectar um número e já poder atender pelo CRM.",
    },
  },
];

// -- Utilitrios ----------------------------------------------------------------

function SectionCard({ icon: Icon, title, subtitle, active, right, children, testid }) {
  return (
    <div data-testid={testid} className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-[#25D366]/15 text-[#25D366]" : "bg-slate-800 text-slate-400"}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-slate-50" style={{ fontFamily: "Outfit" }}>
                {title}
              </h3>
              {active && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#25D366]">
                  <CheckCircle2 className="w-3 h-3" /> Ativo
                </span>
              )}
            </div>
            {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function MaskedInput({ value, masked, onChange, placeholder, testid }) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="relative">
      <Input
        data-testid={testid}
        type={editing || !masked ? "text" : "password"}
        value={editing ? (value || "") : (masked || "")}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setEditing(true)}
        onBlur={() => setEditing(false)}
        placeholder={placeholder}
        className="bg-slate-800 border-slate-700 pr-20 text-sm text-slate-100"
      />
      {masked && !editing && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 font-mono">salvo</span>
      )}
    </div>
  );
}

// -- Settings root --------------------------------------------------------------

export default function Settings() {
  const [channels, setChannels] = useState(null);
  const [metaStatus, setMetaStatus] = useState(null); // status das integrações Meta OAuth
  const [busy, setBusy] = useState(false);
  const [healthAlerts, setHealthAlerts] = useState([]);
  const { on } = useWS();
  const { user } = useAuth();
  const canUseMetaOfficial = hasFeature(user, FEATURE_KEYS.META_OFFICIAL);
  const canUseWhatsAppMeta = canUseMetaOfficial || hasFeature(user, FEATURE_KEYS.WHATSAPP_META);
  const canUseInstagram = canUseMetaOfficial || hasFeature(user, FEATURE_KEYS.INSTAGRAM_DIRECT);
  const canUseFacebook = canUseMetaOfficial || hasFeature(user, FEATURE_KEYS.FACEBOOK_MESSENGER);
  const canUseAnyMeta = canUseWhatsAppMeta || canUseInstagram || canUseFacebook;

  const reload = async () => {
    const { data } = await api.get("/channels");
    setChannels(data);
  };

  const reloadMetaStatus = useCallback(async () => {
    if (!canUseAnyMeta) {
      setMetaStatus(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/meta/status");
      setMetaStatus(data);
    } catch {
      // silencioso - rota pode não existir em staging
    }
  }, [canUseAnyMeta]);

  useEffect(() => { reload(); }, []);
  useEffect(() => { reloadMetaStatus(); }, [reloadMetaStatus]);

  // L parmetros da URL aps callback OAuth Meta
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("meta_success")) {
      const provider = params.get("meta_success");
      const isMock = params.get("meta_mock") === "true";
      toast.success(
        isMock
          ? `${provider} conectado (modo desenvolvimento)`
          : `${provider} conectado com sucesso via Meta!`
      );
      reloadMetaStatus();
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("meta_error")) {
      toast.error(`Erro ao conectar: ${params.get("meta_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [reloadMetaStatus]);

  useEffect(() => {
    const unsub1 = on("evolution.status", () => reload());
    const unsub2 = on("channel.health", (p) => {
      if (p.status === "error") {
        if (p.channel === "meta_whatsapp") {
          toast.error(p.message || "Token Meta/WABA expirado");
          setChannels((current) => {
            if (!current?.meta_whatsapp) return current;
            return {
              ...current,
              meta_whatsapp: {
                ...current.meta_whatsapp,
                enabled: false,
                instances: (current.meta_whatsapp.instances || []).map((item) => ({
                  ...item,
                  status: "inactive",
                })),
              },
            };
          });
        }
        setHealthAlerts((prev) => {
          if (prev.find((a) => a.channel === p.channel)) return prev;
          return [...prev, p];
        });
      }
    });
    return () => { unsub1?.(); unsub2?.(); };
  }, [on]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  };

  const saveSection = async (section, patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/channels", { [section]: patch });
      setChannels(data);
      toast.success("Configurações salvas");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setBusy(false);
    }
  };

  const saveProductivity = async (patch) => {
    setBusy(true);
    try {
      const payload = {
        ...patch,
        scheduling_shortcuts: patch?.schedule_shortcuts || [],
      };
      const { data } = await api.post("/settings/productivity", payload);
      applyProductivity(data);
      toast.success("Configurações de produtividade atualizadas");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao salvar produtividade");
    }
    setBusy(false);
  };

  const applyProductivity = (data) => {
    setChannels((current) => ({ ...(current || {}), productivity: data }));
    window.dispatchEvent(new CustomEvent("productivity-settings-updated", { detail: data }));
  };

  const saveOfficialTemplate = async (template) => {
    setBusy(true);
    try {
      const { data } = await api.post("/settings/productivity/waba-templates", template);
      applyProductivity(data);
      toast.success("Template oficial salvo como rascunho");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao salvar template oficial");
    }
    setBusy(false);
  };

  const submitOfficialTemplate = async (template) => {
    setBusy(true);
    try {
      await api.post("/settings/productivity/waba-templates", template);
      const { data } = await api.post(`/settings/productivity/waba-templates/${template.id}/submit`);
      applyProductivity(data);
      toast.success("Template enviado para anlise");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao enviar template para anlise");
    }
    setBusy(false);
  };

  const deleteOfficialTemplate = async (templateId) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/settings/productivity/waba-templates/${templateId}`);
      applyProductivity(data);
      toast.success("Template oficial removido");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao remover template oficial");
    }
    setBusy(false);
  };

  const syncOfficialTemplateStatuses = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/settings/productivity/waba-templates/sync-status");
      applyProductivity(data);
      toast.success("Status dos templates sincronizado");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao sincronizar status dos templates");
    }
    setBusy(false);
  };
  // -- Handlers OAuth Meta ------------------------------------------------------

  const handleMetaConnect = async (provider) => {
    try {
      const { data } = await api.get(`/auth/meta/${provider}`);
      if (data.mock) {
        toast.info(data.message || "Configure META_APP_ID no .env para OAuth real");
        await reloadMetaStatus();
        return;
      }
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Erro ao iniciar conexão com ${provider}`);
      throw e;
    }
  };

  const handleMetaDisconnect = async (provider) => {
    await api.post("/auth/meta/disconnect", { provider });
    await reloadMetaStatus();
  };

  // Tour geral da tela (cobre as 3 abas sem depender de trocar de aba
  // durante o tour, que exigiria orquestrar clique programático + timing de
  // render -- mais frágil do que vale a pena aqui). Só dispara depois que
  // `channels` carrega, já que antes disso a tela mostra só um spinner.
  const { start: startSettingsTour } = useProductTour("settings", SETTINGS_TOUR_STEPS, {
    autoStart: !!channels,
  });

  if (!channels) {
    return (
      <div className="flex h-screen bg-slate-950">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        <Navbar title="Configurações" subtitle="Integrações de canais, webhooks e automaes" />

        <div className="px-6 py-6 max-w-5xl">
          {/* Alertas de sade */}
          {healthAlerts.length > 0 && (
            <div className="mb-5 space-y-2">
              {healthAlerts.map((a) => (
                <div key={a.channel} className="flex items-start justify-between gap-3 bg-amber-500/10 border border-amber-500/25 rounded-xl px-4 py-3">
                  <div className="flex items-start gap-2">
                    <RefreshCw className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-300">{a.message}</p>
                  </div>
                  <button
                    onClick={() => setHealthAlerts((prev) => prev.filter((x) => x.channel !== a.channel))}
                    className="text-amber-500 hover:text-amber-200 shrink-0"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <Tabs defaultValue="channels" className="space-y-5">
            <TabsList data-testid="settings-tabs-list" className="bg-slate-900/70 border border-slate-800">
              <TabsTrigger value="channels" data-testid="tab-channels" className="data-[state=active]:bg-[#25D366] data-[state=active]:text-slate-950">Canais</TabsTrigger>
              <TabsTrigger value="integrations" data-testid="tab-integrations" className="data-[state=active]:bg-[#25D366] data-[state=active]:text-slate-950">Produtividade</TabsTrigger>
              <TabsTrigger value="mass-campaigns" data-testid="tab-mass-campaigns" className="data-[state=active]:bg-[#25D366] data-[state=active]:text-slate-950">Envio em Massa</TabsTrigger>
            </TabsList>

            <TabsContent value="channels" className="space-y-5">
              {canUseWhatsAppMeta && (
                <>
                  {/* - WhatsApp Oficial Meta - */}
                  <MetaIntegrationCard
                    provider="whatsapp"
                    title="WhatsApp Oficial Meta"
                    description="Conecte números oficiais do WhatsApp Business usando a plataforma oficial da Meta. Ideal para atendimento em escala com templates aprovados."
                    icon={MessageCircle}
                    statusData={metaStatus?.whatsapp}
                    onConnect={() => handleMetaConnect("whatsapp")}
                    onDisconnect={() => handleMetaDisconnect("whatsapp")}
                    onRefreshStatus={reloadMetaStatus}
                    webhookUrl={`${PUBLIC_WEBHOOK_BASE_URL}/api/whatsapp/webhook`}
                    logs={metaStatus?.whatsapp?.logs || []}
                    advancedContent={
                      <MetaWhatsappAdvanced
                        data={channels.meta_whatsapp}
                        onSave={(patch) => saveSection("meta_whatsapp", patch)}
                        busy={busy}
                      />
                    }
                  />

                </>
              )}

              {/* - WhatsApp QR (nativo Baileys) - */}
              <NativeWhatsAppSection />


              {(canUseInstagram || canUseFacebook) && (
                <>
                  {canUseInstagram && (
                    <MetaIntegrationCard
                    provider="instagram"
                    title="Instagram Direct"
                    description="Receba e responda mensagens do Instagram profissional conectado à sua Página do Facebook. Centralize conversas no CRM."
                    icon={Instagram}
                    statusData={metaStatus?.instagram}
                    onConnect={() => handleMetaConnect("instagram")}
                    onDisconnect={() => handleMetaDisconnect("instagram")}
                    onRefreshStatus={reloadMetaStatus}
                    webhookUrl={`${PUBLIC_WEBHOOK_BASE_URL}/api/webhooks/instagram`}
                    logs={metaStatus?.instagram?.logs || []}
                    advancedContent={
                      <InstagramAdvanced
                        data={channels.instagram}
                        onSave={(patch) => saveSection("instagram", patch)}
                        busy={busy}
                      />
                    }
                  />
                  )}

                  {canUseFacebook && (
                    <MetaIntegrationCard
                    provider="facebook"
                    title="Facebook Messenger"
                    description="Conecte sua Página do Facebook para centralizar conversas do Messenger no CRM e responder em tempo real."
                    icon={Facebook}
                    statusData={metaStatus?.facebook}
                    onConnect={() => handleMetaConnect("facebook")}
                    onDisconnect={() => handleMetaDisconnect("facebook")}
                    onRefreshStatus={reloadMetaStatus}
                    webhookUrl={`${PUBLIC_WEBHOOK_BASE_URL}/api/webhooks/facebook`}
                    logs={metaStatus?.facebook?.logs || []}
                    advancedContent={
                      <FacebookAdvanced
                        data={channels.facebook}
                        onSave={(patch) => saveSection("facebook", patch)}
                        busy={busy}
                      />
                    }
                  />
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="integrations" className="space-y-5">
              <ProductivitySettings
                data={channels.productivity}
                metaWhatsApp={channels.meta_whatsapp}
                onSave={saveProductivity}
                onSaveOfficialTemplate={saveOfficialTemplate}
                onSubmitOfficialTemplate={submitOfficialTemplate}
                onDeleteOfficialTemplate={deleteOfficialTemplate}
                onSyncOfficialTemplates={syncOfficialTemplateStatuses}
                busy={busy}
              />
            </TabsContent>

            <TabsContent value="mass-campaigns" className="space-y-5">
              <MassCampaignSettings />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <TourRestartButton onClick={startSettingsTour} label="Rever tour de Configurações" />
    </div>
  );
}

// -- Configurações avanadas para WhatsApp Oficial (multi-instância) ------------

function MetaWhatsappAdvanced({ data, onSave, busy }) {
  const buildInitialRows = React.useCallback(() => {
    const rows = Array.isArray(data?.instances) && data.instances.length > 0
      ? data.instances
      : (data?.phone_number_id || data?.waba_id || data?.access_token_masked)
        ? [{
            id: "legacy-meta-instance",
            label: "Número principal",
            enabled: !!data?.enabled,
            status: data?.enabled ? "active" : "inactive",
            phone_number_id: data?.phone_number_id || "",
            waba_id: data?.waba_id || "",
            access_token: "",
            access_token_masked: data?.access_token_masked || "",
            verify_token: FIXED_META_VERIFY_TOKEN,
          }]
        : [];
    return rows.map((row) => ({
      id: row.id || crypto.randomUUID(),
      label: row.label || "Número Meta",
      enabled: row.enabled ?? true,
      status: row.enabled ? "active" : "inactive",
      phone_number_id: row.phone_number_id || "",
      waba_id: row.waba_id || "",
      access_token: "",
      access_token_masked: row.access_token_masked || "",
      verify_token: FIXED_META_VERIFY_TOKEN,
    }));
  }, [data]);

  const [rows, setRows] = useState(buildInitialRows);
  const removingIdRef = useRef(null);

  useEffect(() => {
    if (removingIdRef.current) return;
    setRows(buildInitialRows());
  }, [buildInitialRows]);

  const patchRow = (id, patch) =>
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const addRow = () =>
    setRows((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        label: `Número Meta ${current.length + 1}`,
        enabled: true,
        status: "inactive",
        phone_number_id: "",
        waba_id: "",
        access_token: "",
        access_token_masked: "",
        verify_token: FIXED_META_VERIFY_TOKEN,
      },
    ]);

  const buildPatch = (sourceRows) => {
    const instances = sourceRows.map((row) => {
      const next = {
        id: row.id,
        label: row.label,
        enabled: row.enabled,
        status: row.enabled ? "active" : "inactive",
        phone_number_id: row.phone_number_id,
        waba_id: row.waba_id,
        verify_token: FIXED_META_VERIFY_TOKEN,
      };
      if (row.access_token) next.access_token = row.access_token;
      return next;
    });
    const enabled = instances.length > 0 && instances.some((row) => row.enabled);
    return { enabled, instances };
  };

  const removeRow = async (id) => {
    removingIdRef.current = id;
    const nextRows = rows.filter((row) => row.id !== id);
    setRows(nextRows);
    try {
      await onSave(buildPatch(nextRows));
    } finally {
      removingIdRef.current = null;
    }
  };

  const save = () => onSave(buildPatch(rows));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] text-slate-500">
          Configuração manual de Phone Number ID, WABA ID e Access Token.
          <br />
          <span className="text-amber-400">Use apenas se o OAuth ainda não estiver configurado.</span>
        </p>
        <Button onClick={addRow} className="bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold text-xs h-7 px-2">
          <Plus className="w-3 h-3 mr-1" /> Adicionar
        </Button>
      </div>

      {rows.length === 0 ? (
        <p className="text-[11px] text-slate-600 italic text-center py-3">
          Nenhuma instância manual configurada.
        </p>
      ) : rows.map((row, index) => (
        <div key={row.id} className="rounded-xl border border-slate-700 bg-slate-950/50 p-3">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Switch checked={row.enabled} onCheckedChange={(v) => patchRow(row.id, { enabled: v, status: v ? "active" : "inactive" })} />
              <span className="text-xs font-semibold text-slate-200">{row.label || `Número Meta ${index + 1}`}</span>
            </div>
            <button onClick={() => removeRow(row.id)} className="text-slate-600 hover:text-red-400 transition">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-600">Nome</Label>
              <Input value={row.label} onChange={(e) => patchRow(row.id, { label: e.target.value })} placeholder="Número Vendas" className="mt-1 bg-slate-800 border-slate-700 text-slate-100 text-xs h-8" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-600">Phone Number ID</Label>
              <Input value={row.phone_number_id} onChange={(e) => patchRow(row.id, { phone_number_id: e.target.value })} placeholder="123456789012345" className="mt-1 bg-slate-800 border-slate-700 text-slate-100 text-xs h-8 font-mono" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-600">WABA ID</Label>
              <Input value={row.waba_id} onChange={(e) => patchRow(row.id, { waba_id: e.target.value })} placeholder="987654321098765" className="mt-1 bg-slate-800 border-slate-700 text-slate-100 text-xs h-8 font-mono" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-600">Verify Token</Label>
              <Input readOnly value={FIXED_META_VERIFY_TOKEN} className="mt-1 bg-slate-950 border-slate-800 text-slate-400 text-xs h-8 font-mono" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-slate-600">Access Token</Label>
              <MaskedInput value={row.access_token} masked={row.access_token_masked} onChange={(v) => patchRow(row.id, { access_token: v })} placeholder="EAAG..." />
            </div>
          </div>
        </div>
      ))}

      <div className="flex items-center justify-between mt-2">
        <a href="https://developers.facebook.com/docs/whatsapp/cloud-api" target="_blank" rel="noreferrer" className="text-[10px] text-slate-600 hover:text-[#25D366] inline-flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> Meta Cloud API Docs
        </a>
        <Button onClick={save} disabled={busy} className="bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold text-xs h-7 px-3">
          <Save className="w-3 h-3 mr-1" /> Salvar configuração manual
        </Button>
      </div>
    </div>
  );
}

// -- Configurações avanadas para Instagram ------------------------------------

function InstagramAdvanced({ data, onSave, busy }) {
  const [form, setForm] = useState({
    enabled: false, page_id: "", access_token: "", instagram_webhook_url: "",
  });

  useEffect(() => {
    setForm({
      enabled: !!data?.enabled,
      page_id: data?.page_id || "",
      access_token: "",
      instagram_webhook_url: data?.instagram_webhook_url || "",
    });
  }, [data]);

  const save = () => {
    const payload = { enabled: form.enabled, page_id: form.page_id, instagram_webhook_url: form.instagram_webhook_url };
    if (form.access_token) payload.access_token = form.access_token;
    onSave(payload);
  };

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-amber-400 mb-3">
        Use apenas se o OAuth ainda no estiver configurado.
      </p>
      <div className="flex items-center gap-2 mb-2">
        <Switch data-testid="instagram-enabled" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
        <span className="text-xs text-slate-400">Habilitar via configuração manual</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-600">Instagram Page ID</Label>
          <Input data-testid="instagram-page-id" value={form.page_id} onChange={(e) => setForm({ ...form, page_id: e.target.value })} placeholder="17841..." className="bg-slate-800 border-slate-700 mt-1 text-slate-100 text-xs h-8 font-mono" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-600">Access Token</Label>
          <MaskedInput testid="instagram-token" value={form.access_token} masked={data?.access_token_masked} onChange={(v) => setForm({ ...form, access_token: v })} placeholder="EAAG..." />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-slate-600">Webhook URL</Label>
          <Input data-testid="instagram-webhook-url" value={form.instagram_webhook_url} onChange={(e) => setForm({ ...form, instagram_webhook_url: e.target.value })} placeholder="https://api.clivercrm.com.br/api/webhooks/instagram" className="bg-slate-800 border-slate-700 mt-1 text-slate-100 text-xs h-8 font-mono" />
        </div>
      </div>
      <div className="flex justify-end mt-2">
        <Button data-testid="instagram-save" onClick={save} disabled={busy} className="bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold text-xs h-7 px-3">
          <Save className="w-3 h-3 mr-1" /> Salvar
        </Button>
      </div>
    </div>
  );
}

// -- Configurações avanadas para Facebook Messenger --------------------------

function FacebookAdvanced({ data, onSave, busy }) {
  const [form, setForm] = useState({
    enabled: false, page_id: "", access_token: "", facebook_webhook_url: "",
  });

  useEffect(() => {
    setForm({
      enabled: !!data?.enabled,
      page_id: data?.page_id || "",
      access_token: "",
      facebook_webhook_url: data?.facebook_webhook_url || "",
    });
  }, [data]);

  const save = () => {
    const payload = { enabled: form.enabled, page_id: form.page_id, facebook_webhook_url: form.facebook_webhook_url };
    if (form.access_token) payload.access_token = form.access_token;
    onSave(payload);
  };

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-amber-400 mb-3">
        Use apenas se o OAuth ainda no estiver configurado.
      </p>
      <div className="flex items-center gap-2 mb-2">
        <Switch data-testid="facebook-enabled" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
        <span className="text-xs text-slate-400">Habilitar via configuração manual</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-600">Facebook Page ID</Label>
          <Input data-testid="facebook-page-id" value={form.page_id} onChange={(e) => setForm({ ...form, page_id: e.target.value })} placeholder="10321..." className="bg-slate-800 border-slate-700 mt-1 text-slate-100 text-xs h-8 font-mono" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-600">Access Token</Label>
          <MaskedInput testid="facebook-token" value={form.access_token} masked={data?.access_token_masked} onChange={(v) => setForm({ ...form, access_token: v })} placeholder="EAAG..." />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-slate-600">Webhook URL</Label>
          <Input data-testid="facebook-webhook-url" value={form.facebook_webhook_url} onChange={(e) => setForm({ ...form, facebook_webhook_url: e.target.value })} placeholder="https://api.clivercrm.com.br/api/webhooks/facebook" className="bg-slate-800 border-slate-700 mt-1 text-slate-100 text-xs h-8 font-mono" />
        </div>
      </div>
      <div className="flex justify-end mt-2">
        <Button data-testid="facebook-save" onClick={save} disabled={busy} className="bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold text-xs h-7 px-3">
          <Save className="w-3 h-3 mr-1" /> Salvar
        </Button>
      </div>
    </div>
  );
}

// -- WhatsApp Nativo (QR Code / Baileys) --------------------------------------

const STATUS_CONFIG = {
  connected:    { label: "Conectado",        color: "text-[#25D366]",  bg: "bg-[#25D366]/15",  dot: "bg-[#25D366]" },
  qr_ready:     { label: "Aguardando scan",  color: "text-yellow-400", bg: "bg-yellow-400/15", dot: "bg-yellow-400" },
  waiting_qr:   { label: "Aguardando QR",   color: "text-yellow-400", bg: "bg-yellow-400/15", dot: "bg-yellow-400 animate-pulse" },
  connecting:   { label: "Conectando...",      color: "text-sky-400",    bg: "bg-sky-400/15",    dot: "bg-sky-400 animate-pulse" },
  initializing: { label: "Inicializando",    color: "text-slate-400",  bg: "bg-slate-700/40",  dot: "bg-slate-400 animate-pulse" },
  disconnected: { label: "Desconectado",     color: "text-slate-400",  bg: "bg-slate-700/30",  dot: "bg-slate-500" },
  logged_out:   { label: "Sessão encerrada", color: "text-red-400",    bg: "bg-red-400/15",    dot: "bg-red-400" },
  qr_expired:   { label: "QR expirado",      color: "text-orange-400", bg: "bg-orange-400/15", dot: "bg-orange-400" },
  error:        { label: "Erro",             color: "text-red-400",    bg: "bg-red-400/15",    dot: "bg-red-400" },
};

function normalizeQrImage(raw) {
  if (!raw || typeof raw !== "string") return null;
  const value = raw.trim();
  if (value.startsWith("data:image/")) return value;
  if (value.startsWith("<svg")) {
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(value)))}`;
  }
  if (/^[A-Za-z0-9+/=\s]+$/.test(value) && value.length > 100) {
    return `data:image/png;base64,${value.replace(/\s+/g, "")}`;
  }
  return null;
}

// Timeout (ms) aguardando o primeiro QR Code - Baileys leva ~5-15s normalmente,
// damos 42s para cobrir dois ciclos de refresh do QR (~20s cada).
const QR_TIMEOUT_MS = 42_000;

function ConnectModal({ open, onClose, onCreated, wsOn }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [instanceId, setInstanceId] = useState(null);
  const instanceIdRef = useRef(null);
  const [qrImage, setQrImage] = useState(null);
  const [connected, setConnected] = useState(false);
  // Estados de erro/timeout
  const [timedOut, setTimedOut] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // Timer ref para o timeout do QR
  const timeoutRef = useRef(null);

  const _clearQrTimer = () => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
  };

  const _startQrTimer = () => {
    _clearQrTimer();
    timeoutRef.current = setTimeout(() => setTimedOut(true), QR_TIMEOUT_MS);
  };

  // Poll REST fallback: se o evento WebSocket for perdido (rede lenta, cold
  // start do Render), busca o QR direto do serviço após QR_POLL_DELAY_MS.
  const QR_POLL_DELAY_MS = 8_000;
  const pollRef = useRef(null);

  const _clearPollTimer = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const _startQrPoll = (id) => {
    _clearPollTimer();
    // Aguarda um pouco antes de começar a polling para dar chance ao WS chegar
    const start = Date.now();
    pollRef.current = setInterval(async () => {
      // Só faz polling se ainda não temos QR e ainda estamos no step 2
      if (!instanceIdRef.current || instanceIdRef.current !== id) { _clearPollTimer(); return; }
      if (Date.now() - start < QR_POLL_DELAY_MS) return; // aguarda delay inicial
      try {
        const { data } = await api.get(`/whatsapp/instances/${id}/qr`);
        if (data?.qr) {
          const nextQr = normalizeQrImage(data.qr);
          if (nextQr) {
            _clearQrTimer();
            _clearPollTimer();
            setTimedOut(false);
            setQrImage(nextQr);
          }
        }
      } catch {
        // 404 = QR ainda não disponível ou expirado - continua polling
        // outros erros: ignora silenciosamente
      }
    }, 3_000); // verifica a cada 3s
  };

  useEffect(() => {
    if (!open) return;
    const unsubQr = wsOn("whatsapp.qr", (p) => {
      if (p.instanceId !== instanceIdRef.current) return;
      const nextQr = normalizeQrImage(p.qr);
      if (nextQr) {
        _clearQrTimer();        // QR chegou via WS - cancela timeout e polling
        _clearPollTimer();
        setTimedOut(false);
        setQrImage(nextQr);
      } else {
        toast.error("Não foi possível ler o QR Code recebido. Tente gerar novamente.");
      }
    });
    const unsubStatus = wsOn("whatsapp.status", (p) => {
      if (p.instanceId !== instanceIdRef.current || p.status !== "connected") return;
      _clearQrTimer();
      _clearPollTimer();
      setConnected(true);
      toast.success(` WhatsApp conectado: +${p.phone || instanceIdRef.current}`);
      onCreated?.({ instanceId: instanceIdRef.current, name, status: "connected", phone: p.phone });
      setTimeout(() => { onClose(); }, 1800);
    });
    return () => { unsubQr?.(); unsubStatus?.(); };
  }, [open, wsOn, onClose, onCreated, name]);

  const handleClose = () => {
    _clearQrTimer();
    _clearPollTimer();
    setStep(1); setName(""); setBusy(false);
    instanceIdRef.current = null;
    setInstanceId(null); setQrImage(null); setConnected(false);
    setTimedOut(false); setRetrying(false);
    onClose();
  };

  const handleCreate = async () => {
    if (!name.trim()) return toast.error("Digite um nome para o número");
    const id = name.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    setBusy(true);
    instanceIdRef.current = id;
    setInstanceId(id);
    try {
      await api.post("/whatsapp/instances", { instance_id: id, name: name.trim() });
      setStep(2);
      _startQrTimer();
      _startQrPoll(id);   // fallback REST caso o evento WS seja perdido
    } catch (e) {
      instanceIdRef.current = null;
      setInstanceId(null);
      toast.error(formatApiError(e?.response?.data?.detail || e?.response?.data?.error) || "Erro ao criar instância");
    }
    setBusy(false);
  };

  // Reinicia a instância via POST .../restart e aguarda novo QR
  const handleRetry = async () => {
    if (!instanceIdRef.current) return;
    setRetrying(true);
    setQrImage(null);
    setTimedOut(false);
    try {
      await api.post(`/whatsapp/instances/${instanceIdRef.current}/restart`, {});
      _startQrTimer();
      _startQrPoll(instanceIdRef.current);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erro ao reiniciar instância");
      setTimedOut(true);   // permanece em estado de erro
    }
    setRetrying(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-sm mx-4">
        {/* -- Cabeçalho ------------------------------------------------------- */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#25D366]/15 flex items-center justify-center">
              <Smartphone className="w-4 h-4 text-[#25D366]" />
            </div>
            <h2 className="text-sm font-bold text-slate-100">
              {step === 1 ? "Conectar Novo Número" : "Escaneie o QR Code"}
            </h2>
          </div>
          <button onClick={handleClose} className="text-slate-400 hover:text-slate-100 transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* -- Conteúdo -------------------------------------------------------- */}
        <div className="px-5 py-5">
          {step === 1 ? (
            /* -- Passo 1: Nome ------------------------------------------------ */
            <>
              <p className="text-xs text-slate-400 mb-4">
                Escolha um nome para identificar este número (ex: Vendas, Suporte, Equipe-SP).
              </p>
              <Label className="text-[11px] uppercase tracking-wider text-slate-400">Nome do número</Label>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder='ex: "Vendas"'
                className="w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#25D366]"
              />
              <Button
                onClick={handleCreate}
                disabled={busy}
                className="w-full mt-4 bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold"
              >
                {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Criando...</> : "Continuar"}
              </Button>
            </>
          ) : (
            /* -- Passo 2: QR Code --------------------------------------------- */
            <>
              {connected ? (
                /*  Conectado */
                <div className="flex flex-col items-center gap-3 py-4">
                  <div className="w-14 h-14 rounded-full bg-[#25D366]/15 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-[#25D366]" />
                  </div>
                  <p className="text-sm font-semibold text-slate-100">Conectado com sucesso!</p>
                </div>
              ) : timedOut ? (
                /*  Timeout - QR não chegou a tempo */
                <div className="flex flex-col items-center gap-4 py-4">
                  <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-red-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-slate-100">QR Code não chegou</p>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                      O serviço demorou mais que o esperado.<br />Tente reiniciar a instância.
                    </p>
                  </div>
                  <Button
                    onClick={handleRetry}
                    disabled={retrying}
                    className="w-full bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold"
                  >
                    {retrying
                      ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Reiniciando...</>
                      : "Tentar novamente"}
                  </Button>
                  <button
                    onClick={handleClose}
                    className="text-xs text-slate-500 hover:text-slate-300 transition"
                  >
                    Cancelar
                  </button>
                </div>
              ) : qrImage ? (
                /*  QR pronto para escanear */
                <>
                  <p className="text-xs text-slate-400 mb-4 text-center">
                    Abra o WhatsApp -> <strong className="text-slate-200">Aparelhos Conectados</strong> -> Conectar aparelho
                  </p>
                  <div className="flex flex-col items-center bg-white rounded-xl p-3 mx-auto w-fit">
                    <img src={qrImage} alt="QR Code" className="w-44 h-44 object-contain" />
                  </div>
                  <p className="text-[10px] text-slate-500 text-center mt-2">
                    QR expira em ~20s - novo QR chegar automaticamente
                  </p>
                  <button
                    onClick={handleRetry}
                    disabled={retrying}
                    className="flex items-center gap-1 mx-auto mt-3 text-[11px] text-slate-500 hover:text-slate-300 transition disabled:opacity-50"
                  >
                    {retrying
                      ? <><Loader2 className="w-3 h-3 animate-spin" /> Reiniciando...</>
                      : <><RefreshCw className="w-3 h-3" /> Regenerar QR</>
                    }
                  </button>
                </>
              ) : (
                /*  Aguardando QR */
                <div className="flex flex-col items-center gap-3 py-6">
                  <Loader2 className="w-8 h-8 animate-spin text-[#25D366]" />
                  <p className="text-xs text-slate-400">Gerando QR Code...</p>
                  <button
                    onClick={handleClose}
                    className="text-xs text-slate-500 hover:text-slate-300 transition mt-1"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* -- Indicador de passo ---------------------------------------------- */}
        <div className="flex items-center justify-center gap-2 pb-4">
          {[1, 2].map((n) => (
            <div key={n} className={`h-1.5 rounded-full transition-all ${step >= n ? "w-6 bg-[#25D366]" : "w-3 bg-slate-700"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function InstanceRow({ inst, qrImage, onShowQr, onDelete, isAdmin, teamMembers = [], onAssignmentChange }) {
  const st = STATUS_CONFIG[inst.status] ?? STATUS_CONFIG.disconnected;
  const [savingResponsible, setSavingResponsible] = useState(false);
  const [savingShared, setSavingShared] = useState(false);

  const handleResponsibleChange = async (e) => {
    const value = e.target.value; // "" = não atribuído
    setSavingResponsible(true);
    try {
      await onAssignmentChange(inst.instanceId, { responsible_user_id: value || null });
    } finally {
      setSavingResponsible(false);
    }
  };

  const handleSharedToggle = async (checked) => {
    setSavingShared(true);
    try {
      await onAssignmentChange(inst.instanceId, { shared_with_team: checked });
    } finally {
      setSavingShared(false);
    }
  };

  return (
    <>
      <tr className="border-b border-slate-800 hover:bg-slate-800/40 transition">
        <td className="px-4 py-3 text-sm text-slate-100 font-mono">
          {inst.phone ? `+${inst.phone}` : <span className="text-slate-500 italic text-xs">aguardando...</span>}
        </td>
        <td className="px-4 py-3 text-sm text-slate-300">{inst.name || inst.instanceId}</td>
        <td className="px-4 py-3 text-xs text-slate-400">
          {isAdmin ? (
            <select
              value={inst.responsibleUserId || ""}
              onChange={handleResponsibleChange}
              disabled={savingResponsible}
              className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-[#25D366] disabled:opacity-50"
            >
              <option value="">Não atribuído</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>{m.name || m.email}</option>
              ))}
            </select>
          ) : (
            inst.responsibleUserName || inst.createdByUserName || "Não identificado"
          )}
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${st.bg} ${st.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
            {st.label}
          </span>
        </td>
        <td className="px-4 py-3">
          {inst.status === "connected" ? (
            <span className="text-[11px] font-semibold text-[#25D366]">Alta</span>
          ) : (
            <span className="text-[11px] text-slate-600">-</span>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {isAdmin && (
              <label
                title="Compartilhar com o time"
                className="flex items-center gap-1 cursor-pointer"
              >
                <Switch
                  checked={!!inst.sharedWithTeam}
                  onCheckedChange={handleSharedToggle}
                  disabled={savingShared}
                />
              </label>
            )}
            {isAdmin && (
              <HelpTooltip text="Quando ligado, todos os agentes do time veem e podem responder as conversas deste canal, não só o Responsável selecionado." />
            )}
            {inst.status !== "connected" && (
              <button
                title="Mostrar QR"
                onClick={() => onShowQr(inst)}
                className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-100 transition"
              >
                <QrCode className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              title="Remover"
              onClick={() => onDelete(inst.instanceId)}
              className="p-1.5 rounded-lg bg-slate-800 hover:bg-red-500/20 text-slate-400 hover:text-red-400 transition"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
      {qrImage && inst.status !== "connected" && (
        <tr className="bg-slate-950/60">
          <td colSpan={6} className="px-4 py-3">
            <div className="flex items-center gap-4">
              <div className="bg-white rounded-xl p-2">
                <img src={qrImage} alt="QR" className="w-32 h-32 object-contain" />
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Abra o WhatsApp -> <strong className="text-slate-200">Aparelhos Conectados</strong><br />
                -> Conectar aparelho -> escaneie o QR ao lado
              </p>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function NativeWhatsAppSection() {
  const { on } = useWS();
  const { user } = useAuth();
  const isAdmin = ["owner", "admin", "super_admin"].includes(normalizeRole(user?.role));
  const isPlatformSuperAdmin = isSuperAdmin(user);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [qrMap, setQrMap] = useState({});
  const [inlineQr, setInlineQr] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);

  const loadInstances = async () => {
    try {
      const { data } = await api.get("/whatsapp/instances");
      setInstances(data);
    } catch { setInstances([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadInstances(); }, []);

  // Lista de agentes do tenant para o seletor de "Responsável" - só precisa
  // ser carregada para quem pode ver/usar o seletor (owner/admin/super_admin).
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/team")
      .then(({ data }) => setTeamMembers(Array.isArray(data) ? data : []))
      .catch(() => setTeamMembers([]));
  }, [isAdmin]);

  const handleAssignmentChange = async (instanceId, patch) => {
    try {
      const { data } = await api.patch(`/whatsapp/instances/${instanceId}/assignment`, patch);
      setInstances((prev) =>
        prev.map((i) =>
          i.instanceId === instanceId
            ? {
                ...i,
                responsibleUserId: data.responsibleUserId ?? null,
                responsibleUserName: data.responsibleUserName ?? null,
                sharedWithTeam: !!data.sharedWithTeam,
              }
            : i
        )
      );
      toast.success("Canal atualizado");
    } catch (error) {
      toast.error(formatApiError(error?.response?.data?.detail) || "Erro ao atualizar canal");
    }
  };

  useEffect(() => {
    const unsubQr = on("whatsapp.qr", (p) => {
      const nextQr = normalizeQrImage(p.qr);
      if (!nextQr) {
        toast.error("Não foi possível ler o QR Code recebido. Tente gerar novamente.");
        return;
      }
      setQrMap((prev) => ({ ...prev, [p.instanceId]: nextQr }));
      setInstances((prev) =>
        prev.map((i) => i.instanceId === p.instanceId ? { ...i, status: "qr_ready" } : i)
      );
    });
    const unsubStatus = on("whatsapp.status", (p) => {
      setInstances((prev) =>
        prev.map((i) =>
          i.instanceId === p.instanceId
            ? { ...i, status: p.status, phone: p.phone ?? i.phone }
            : i
        )
      );
      if (p.status === "connected") {
        setQrMap((prev) => { const n = { ...prev }; delete n[p.instanceId]; return n; });
        setInlineQr(null);
      }
    });
    return () => { unsubQr?.(); unsubStatus?.(); };
  }, [on]);

  const handleDelete = async (instanceId) => {
    if (!window.confirm("Remover esta instância?")) return;
    try {
      await api.delete(`/whatsapp/instances/${instanceId}`);
      // Remove da UI independente de cleanup parcial - o backend sempre retorna ok:true
      setInstances((prev) => prev.filter((i) => i.instanceId !== instanceId));
      setQrMap((prev) => { const n = { ...prev }; delete n[instanceId]; return n; });
      if (inlineQr === instanceId) setInlineQr(null);
      toast.success("Instância removida");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(formatApiError(detail) || "Erro ao remover instância");
    }
  };

  const handleShowQr = async (inst) => {
    // 1. QR já está em cache local - apenas alterna exibição inline
    if (qrMap[inst.instanceId]) {
      setInlineQr((prev) => (prev === inst.instanceId ? null : inst.instanceId));
      return;
    }

    // 2. Tenta buscar QR via REST (pode ter perdido o evento WebSocket)
    try {
      const { data } = await api.get(`/whatsapp/instances/${inst.instanceId}/qr`);
      if (data?.ok && data?.qr) {
        const normalized = normalizeQrImage(data.qr);
        if (normalized) {
          setQrMap((prev) => ({ ...prev, [inst.instanceId]: normalized }));
          setInlineQr(inst.instanceId);
          return;
        }
      }
    } catch { /* REST falhou - continua para oferecer restart */ }

    // 3. Sem QR disponível - oferece reiniciar a instância para gerar novo QR
    const label = inst.name || inst.instanceId;
    if (window.confirm(`Nenhum QR disponível para "${label}".\n\nDeseja reiniciar a instância para gerar um novo QR Code?`)) {
      try {
        await api.post(`/whatsapp/instances/${inst.instanceId}/restart`);
        toast.success("Instância reiniciada - aguarde o QR aparecer");
        setInstances((prev) =>
          prev.map((i) => i.instanceId === inst.instanceId ? { ...i, status: "initializing" } : i)
        );
        // Limpa QR expirado do mapa
        setQrMap((prev) => { const n = { ...prev }; delete n[inst.instanceId]; return n; });
        setInlineQr(null);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao reiniciar instância");
      }
    }
  };

  const handleCreated = (newInst) => {
    setInstances((prev) => {
      const exists = prev.find((i) => i.instanceId === newInst.instanceId);
      if (exists) return prev.map((i) => i.instanceId === newInst.instanceId ? { ...i, ...newInst } : i);
      return [...prev, newInst];
    });
  };

  const connectedCount = instances.filter((i) => i.status === "connected").length;

  return (
    <>
      <ConnectModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onCreated={handleCreated}
        wsOn={on}
      />

      <SectionCard
        icon={Smartphone}
        title="WhatsApp - Números Conectados"
        subtitle={
          isPlatformSuperAdmin
            ? "Multi-instância nativo via QR Code (sem Evolution API)"
            : "Multi-instância nativo via QR Code"
        }
        active={connectedCount > 0}
        testid="section-native-whatsapp"
        right={
          <Button
            onClick={() => setShowModal(true)}
            className="bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold text-xs h-8"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Conectar Novo Número
          </Button>
        }
      >
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#25D366]" />
          </div>
        ) : instances.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-slate-500" />
            </div>
            <p className="text-sm text-slate-400">Nenhum número conectado ainda.</p>
            <p className="text-xs text-slate-500">
              Clique em <strong className="text-slate-300">Conectar Novo Número</strong> para começar.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-950/70 border-b border-slate-800">
                  <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Número de telefone</th>
                  <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Nome de exibição</th>
                  <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      Responsável
                      <HelpTooltip text="Quem recebe e responde as conversas deste canal. Deixe 'Não atribuído' se todo o time puder atender." />
                    </span>
                  </th>
                  <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Qualidade</th>
                  <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500"></th>
                </tr>
              </thead>
              <tbody>
                {instances.map((inst) => (
                  <InstanceRow
                    key={inst.instanceId}
                    inst={inst}
                    qrImage={inlineQr === inst.instanceId ? qrMap[inst.instanceId] : null}
                    onShowQr={handleShowQr}
                    onDelete={handleDelete}
                    isAdmin={isAdmin}
                    teamMembers={teamMembers}
                    onAssignmentChange={handleAssignmentChange}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {connectedCount > 0 && (
          <p className="text-[11px] text-slate-500 mt-3">
            <CheckCircle2 className="inline w-3 h-3 mr-1 text-[#25D366]" />
            {connectedCount} número{connectedCount > 1 ? "s" : ""} conectado{connectedCount > 1 ? "s" : ""} - mensagens chegando em tempo real
          </p>
        )}
      </SectionCard>
    </>
  );
}

// -- Google OAuth --------------------------------------------------------------

function GoogleSection({ data: initialData }) {
  const { on } = useWS();
  const [status, setStatus] = useState({
    calendar_connected: !!initialData?.calendar_connected,
    gmail_connected:    !!initialData?.gmail_connected,
    account_email:      initialData?.account_email || null,
    google_configured:  true,
  });
  const [alerts, setAlerts] = useState({});
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/google/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_success")) {
      toast.success("Google conectado com sucesso!");
      api.get("/google/status").then(({ data }) => setStatus(data)).catch(() => {});
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("google_error")) {
      toast.error(`Erro ao conectar Google: ${params.get("google_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const unsub = on("channel.health", (p) => {
      if (p.status === "error" && (p.channel === "google_calendar" || p.channel === "google_gmail")) {
        setAlerts((prev) => ({ ...prev, [p.channel]: p.message }));
        toast.warning(p.message);
        if (p.channel === "google_calendar") setStatus((s) => ({ ...s, calendar_connected: false }));
        if (p.channel === "google_gmail")    setStatus((s) => ({ ...s, gmail_connected: false }));
      }
    });
    return () => unsub?.();
  }, [on]);

  const connectGoogle = async (scopes) => {
    setBusy(scopes);
    try {
      const { data } = await api.get(`/google/auth-url?scopes=${scopes}`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar URL de autenticação Google");
      setBusy(null);
    }
  };

  const disconnectGoogle = async (service) => {
    setBusy(service);
    try {
      await api.delete(`/google/disconnect?service=${service}`);
      if (service === "calendar") setStatus((s) => ({ ...s, calendar_connected: false }));
      if (service === "gmail")    setStatus((s) => ({ ...s, gmail_connected: false }));
      setAlerts((prev) => { const n = { ...prev }; delete n[`google_${service}`]; return n; });
      toast.success(`Google ${service === "calendar" ? "Agenda" : "Gmail"} desconectado`);
    } catch { toast.error("Erro ao desconectar"); }
    setBusy(null);
  };

  const calAlert = alerts["google_calendar"];
  const gmailAlert = alerts["google_gmail"];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <SectionCard
        icon={Calendar}
        title="Google Agenda"
        subtitle="Agende reunies direto do chat"
        active={status.calendar_connected}
        testid="section-google-calendar"
      >
        {calAlert && (
          <div className="mb-3 flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-300">
            <RefreshCw className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {calAlert}
          </div>
        )}
        <p className="text-xs text-slate-400 mb-3">
          Sincronize eventos de reunio no calendrio do lead com um clique.
          {!status.google_configured && (
            <span className="block mt-1 text-amber-400">
              Configure GOOGLE_CLIENT_ID no .env do backend.
            </span>
          )}
        </p>
        {status.account_email && status.calendar_connected && (
          <p className="text-[11px] text-slate-500 mb-2">
            <CheckCircle2 className="inline w-3 h-3 mr-1 text-[#25D366]" />
            {status.account_email}
          </p>
        )}
        <div className="flex gap-2">
          <Button
            data-testid="connect-google-calendar"
            onClick={() => status.calendar_connected ? disconnectGoogle("calendar") : connectGoogle("calendar")}
            disabled={busy === "calendar"}
            className={`flex-1 ${status.calendar_connected
              ? "bg-slate-800 hover:bg-red-500/20 text-slate-300 hover:text-red-300 border border-slate-700"
              : "bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700"}`}
          >
            {busy === "calendar"
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Aguarde...</>
              : status.calendar_connected
              ? <><WifiOff className="w-4 h-4 mr-1.5" /> Desconectar</>
              : <><Link2 className="w-4 h-4 mr-1.5" /> Conectar Google Agenda</>}
          </Button>
          {!status.calendar_connected && (
            <Button
              onClick={() => connectGoogle("calendar")}
              disabled={!status.google_configured || busy === "calendar"}
              className="bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold"
            >
              <Link2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </SectionCard>

      <SectionCard
        icon={Mail}
        title="Gmail - Omni-Email"
        subtitle="Sincronizao bidirecional de e-mails"
        active={status.gmail_connected}
        testid="section-google-gmail"
      >
        {gmailAlert && (
          <div className="mb-3 flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-300">
            <RefreshCw className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {gmailAlert}
          </div>
        )}
        <p className="text-xs text-slate-400 mb-3">
          E-mails do lead aparecem na timeline e respostas saem pelo Gmail da conta conectada.
        </p>
        {status.account_email && status.gmail_connected && (
          <p className="text-[11px] text-slate-500 mb-2">
            <CheckCircle2 className="inline w-3 h-3 mr-1 text-[#25D366]" />
            {status.account_email}
          </p>
        )}
        <Button
          data-testid="connect-gmail"
          onClick={() => status.gmail_connected ? disconnectGoogle("gmail") : connectGoogle("gmail")}
          disabled={busy === "gmail" || !status.google_configured}
          className={`w-full ${status.gmail_connected
            ? "bg-slate-800 hover:bg-red-500/20 text-slate-300 hover:text-red-300 border border-slate-700"
            : "bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700"}`}
        >
          {busy === "gmail"
            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Aguarde...</>
            : status.gmail_connected
            ? <><WifiOff className="w-4 h-4 mr-1.5" /> Desconectar Gmail</>
            : <><Link2 className="w-4 h-4 mr-1.5" /> Conectar Gmail</>}
        </Button>
      </SectionCard>
    </div>
  );
}
